import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getSigningData, submitSignature, uploadSigningFile, getUploadUrl } from "@/lib/api";
import { AU_STATES, RENT_MODE_LABELS, TIME_PERIOD_LABELS, formatDateTime } from "@/lib/constants";
import { getErrorMessage } from "@/lib/utils";
import { CheckCircle, IdentificationCard, UploadSimple, SpinnerGap } from "@phosphor-icons/react";
import BrandLogo from "@/components/BrandLogo";

function InputField({ label, testId, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">{label}</label>
      <input data-testid={testId} className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black font-medium" {...props} />
    </div>
  );
}

export default function AgreementSignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    mobile: "",
    address: "",
    suburb: "",
    state: "NSW",
    postcode: "",
    licence_number: "",
    licence_state: "NSW",
    licence_expiry: "",
    licence_photo_id: "",
  });

  useEffect(() => {
    getSigningData(token)
      .then((payload) => {
        setData(payload);
        if (payload.status === "SIGNED") {
          setSigned(true);
        }
        const customer = payload.customer || {};
        setForm((current) => ({
          ...current,
          full_name: customer.full_name || payload.customer_name || "",
          mobile: customer.mobile || "",
          address: customer.address || "",
          suburb: customer.suburb || "",
          state: customer.state || "NSW",
          postcode: customer.postcode || "",
          licence_number: customer.licence_number || "",
          licence_state: customer.licence_state || "NSW",
          licence_expiry: customer.licence_expiry || "",
          licence_photo_id: customer.licence_photo_id || "",
        }));
      })
      .catch(() => setError("Agreement not found or link has expired"))
      .finally(() => setLoading(false));
  }, [token]);

  const formError = useMemo(() => {
    if (!form.full_name.trim()) return "Enter your full name.";
    if (!form.mobile.trim()) return "Enter your mobile number.";
    if (!form.licence_number.trim()) return "Enter your licence number.";
    if (!form.licence_photo_id) return "Upload a licence photo before signing.";
    return "";
  }, [form]);

  const startDraw = (event) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (event.touches ? event.touches[0].clientX : event.clientX) - rect.left;
    const y = (event.touches ? event.touches[0].clientY : event.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (event) => {
    if (!isDrawing) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (event.touches ? event.touches[0].clientX : event.clientX) - rect.left;
    const y = (event.touches ? event.touches[0].clientY : event.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0F172A";
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDraw = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const upload = await uploadSigningFile(token, file);
      setForm((current) => ({ ...current, licence_photo_id: upload.file_id }));
    } catch (err) {
      setError(getErrorMessage(err, "Failed to upload licence photo"));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleSign = async () => {
    if (!hasSignature || formError) {
      setError(formError || "Add your signature to continue.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const signatureData = canvasRef.current.toDataURL("image/png");
      await submitSignature(token, { ...form, signature_data: signatureData });
      setSigned(true);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to sign"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><div className="text-sm text-slate-400">Loading agreement...</div></div>;
  }
  if (error && !data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><div className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-md px-6 py-4">{error}</div></div>;
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-8" data-testid="agreement-signed">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center space-y-4">
          <CheckCircle weight="duotone" className="w-16 h-16 text-emerald-500 mx-auto" />
          <h1 className="text-2xl font-black font-heading text-slate-900">Agreement Signed</h1>
          <p className="text-sm text-slate-500">Thank you, {form.full_name || data?.customer_name}. Your rental agreement for <strong>{data?.rental_number}</strong> has been signed successfully.</p>
          <p className="text-xs text-slate-400">You can close this window. The rental team will be in touch for pickup.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 pb-8 sm:p-8" data-testid="agreement-sign-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <BrandLogo
            iconClassName="w-10 h-10 rounded-lg"
            textClassName="text-lg font-extrabold tracking-tight font-heading text-slate-900"
          />
          <div>
            <p className="text-xs text-slate-500 break-words">Customer details and agreement · {data?.rental_number}</p>
          </div>
        </div>

        {error ? <div data-testid="agreement-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md">{error}</div> : null}

        <div data-testid="agreement-summary" className="bg-white border border-slate-200 rounded-md p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Rental</p>
              <p className="font-semibold text-slate-900">{data?.rental_number}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Vehicle</p>
              <p className="text-slate-700 break-words">{[data?.vehicle_display, data?.vehicle_registration].filter(Boolean).join(" · ") || "Vehicle details unavailable"}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Hire Timing</p>
              <p className="text-slate-700">{data?.pickup_datetime ? formatDateTime(data.pickup_datetime) : "Pickup pending"}</p>
              <p className="text-slate-500">{data?.return_datetime ? `Planned return ${formatDateTime(data.return_datetime)}` : "Open-ended return"}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Billing</p>
              <p className="text-slate-700">{RENT_MODE_LABELS[data?.rent_mode] || data?.rent_mode}</p>
              <p className="text-slate-500">{TIME_PERIOD_LABELS[data?.time_period] || data?.time_period}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6">
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
              <h2 className="text-lg font-bold font-heading text-slate-900">Your Details</h2>
              <InputField label="Email" testId="sign-email" value={data?.customer_email || ""} disabled />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField testId="sign-full-name" label="Full Name" value={form.full_name} onChange={(event) => setForm((current) => ({ ...current, full_name: event.target.value }))} />
                <InputField testId="sign-mobile" label="Mobile" value={form.mobile} onChange={(event) => setForm((current) => ({ ...current, mobile: event.target.value }))} />
                <InputField testId="sign-address" label="Address" value={form.address} onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))} />
                <InputField testId="sign-suburb" label="Suburb" value={form.suburb} onChange={(event) => setForm((current) => ({ ...current, suburb: event.target.value }))} />
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">State</label>
                  <select data-testid="sign-state" value={form.state} onChange={(event) => setForm((current) => ({ ...current, state: event.target.value }))} className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 bg-white font-medium">
                    {AU_STATES.map((state) => <option key={state} value={state}>{state}</option>)}
                  </select>
                </div>
                <InputField testId="sign-postcode" label="Postcode" value={form.postcode} onChange={(event) => setForm((current) => ({ ...current, postcode: event.target.value }))} />
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
              <h2 className="text-lg font-bold font-heading text-slate-900">Licence Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField testId="sign-licence-number" label="Licence Number" value={form.licence_number} onChange={(event) => setForm((current) => ({ ...current, licence_number: event.target.value }))} />
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Licence State</label>
                  <select data-testid="sign-licence-state" value={form.licence_state} onChange={(event) => setForm((current) => ({ ...current, licence_state: event.target.value }))} className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 bg-white font-medium">
                    {AU_STATES.map((state) => <option key={state} value={state}>{state}</option>)}
                  </select>
                </div>
                <InputField testId="sign-licence-expiry" label="Licence Expiry" type="date" value={form.licence_expiry} onChange={(event) => setForm((current) => ({ ...current, licence_expiry: event.target.value }))} />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Licence Photo</label>
                {form.licence_photo_id ? (
                  <a href={getUploadUrl(form.licence_photo_id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 h-11 px-4 border border-slate-200 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    <IdentificationCard weight="bold" className="w-4 h-4" /> View uploaded licence
                  </a>
                ) : null}
                <label className={`mt-3 inline-flex items-center gap-2 h-11 px-4 rounded-md text-sm font-semibold ${uploading ? "bg-slate-100 text-slate-400" : "bg-slate-100 text-slate-700 hover:bg-slate-200 cursor-pointer"}`}>
                  {uploading ? <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Uploading...</> : <><UploadSimple weight="bold" className="w-4 h-4" /> {form.licence_photo_id ? "Replace licence photo" : "Upload licence photo"}</>}
                  <input data-testid="sign-licence-upload" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h2 className="text-lg font-bold font-heading text-slate-900 mb-4">Rental Agreement</h2>
              <pre className="max-h-80 overflow-y-auto whitespace-pre-wrap break-words text-sm font-sans leading-relaxed text-slate-700">{data?.terms_text}</pre>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Your Signature</h3>
              <p data-testid="agreement-sign-instructions" className="text-sm text-slate-500">Complete your details, upload your licence photo, then sign in the box below.</p>
              <div className="border-2 border-dashed border-slate-300 rounded-md overflow-hidden bg-slate-50">
                <canvas
                  ref={canvasRef}
                  data-testid="signature-canvas"
                  width={600}
                  height={200}
                  className="w-full h-48 cursor-crosshair touch-none"
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={stopDraw}
                  onMouseLeave={stopDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={stopDraw}
                />
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button data-testid="signature-clear" onClick={clearCanvas} className="text-left text-xs font-bold text-slate-500 hover:text-red-500">Clear Signature</button>
                <button data-testid="signature-submit" onClick={handleSign} disabled={!hasSignature || submitting || Boolean(formError)} className="h-12 w-full rounded-md bg-emerald-600 px-8 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-40 sm:w-auto">
                  {submitting ? "Signing..." : "Complete & Sign"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
